import type { StatementModel, WorkbenchState } from '../../utils/useStatementModel';
import { centsToDollars, monthLabel } from '../../utils/useStatementModel';

// Validated categorical palette (light surface): in-base / bonus / fees & materials.
const SEGMENT_COLORS = { base: '#008A66', bonus: '#B8821F', fees: '#3E6ECC' } as const;

interface CompositionBarProps {
  model: StatementModel;
  state: WorkbenchState;
  month: string;
}

/**
 * One stacked bar: where the month's collections went — the visual receipt of
 * the exclusion rules. Toggling a chip visibly moves dollars between segments.
 */
export function CompositionBar({ model, state, month }: CompositionBarProps) {
  const { totals } = model;
  const bonusTotal = model.programs.reduce((s, p) => s + p.bonusCents, 0);

  // When a rule is off, its dollars sit in the base segment (via the add-back line).
  const segments = [
    {
      key: 'base',
      label: 'In rent base',
      cents: totals.baseCents - model.adjustments.reduce((s, a) => s + a.amountCents, 0),
      color: SEGMENT_COLORS.base,
    },
    {
      key: 'bonus',
      label: 'BHCS Cash bonus (excluded)',
      cents: state.excludeBonus ? bonusTotal : 0,
      color: SEGMENT_COLORS.bonus,
    },
    {
      key: 'fees',
      label: 'Fees & materials (excluded)',
      cents: state.excludeFeesMaterials ? totals.feesMaterialsCents : 0,
      color: SEGMENT_COLORS.fees,
    },
  ].filter((s) => s.cents > 0);

  const total = segments.reduce((s, x) => s + x.cents, 0);
  if (total <= 0) return null;

  return (
    <div>
      <div className="mb-1.5 text-sm text-gray-600">
        Where {monthLabel(month)}&rsquo;s <span className="font-medium tabular-nums">${centsToDollars(total)}</span> went
      </div>
      <div className="flex h-5 w-full overflow-hidden rounded" role="img" aria-label="Composition of collections">
        {segments.map((s, i) => (
          <div
            key={s.key}
            title={`${s.label}: $${centsToDollars(s.cents)}`}
            style={{
              width: `${(s.cents / total) * 100}%`,
              backgroundColor: s.color,
              marginLeft: i > 0 ? 2 : 0,
            }}
          />
        ))}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
        {segments.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5">
            <span aria-hidden className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
            {s.label} <span className="tabular-nums text-gray-500">${centsToDollars(s.cents)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
